import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Phone,
  Mail,
  MapPin,
  LogOut,
  Lock,
  Moon,
  Sun,
  ChevronLeft,
  Settings,
  FileText
} from 'lucide-react'
import useAuthStore from '../../stores/useAuthStore'
import ConfirmDialog from '../../components/common/ConfirmDialog'

/**
 * Profile Page - User profile and settings
 */
const Profile = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true'
  )
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  // Handle logout
  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    localStorage.setItem('darkMode', newMode.toString())

    if (newMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    return parts.length >= 2
      ? `${parts[0][0]}${parts[1][0]}`
      : parts[0][0]
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with Profile Card */}
      <header className="bg-gradient-to-br from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 px-4 pt-6 pb-20">
        <h1 className="text-2xl font-bold text-white mb-6">حسابي</h1>

        {/* Profile Avatar & Info */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
            <span className="text-primary-600 font-bold text-2xl">
              {getInitials(user?.name)}
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white mb-1">
              {user?.name || 'المستخدم'}
            </h2>
            <p className="text-primary-100">
              {user?.role === 'admin' ? 'مدير النظام' : 'مندوب'}
            </p>
          </div>
        </div>
      </header>

      {/* Profile Info Card */}
      <main className="px-4 -mt-12 pb-24">
        <div className="card p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
            المعلومات الشخصية
          </h3>

          <div className="space-y-3">
            {/* Username */}
            <div className="flex items-center gap-3 py-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">اسم المستخدم</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {user?.username || 'username'}
                </p>
              </div>
            </div>

            {/* Email */}
            {user?.email && (
              <div className="flex items-center gap-3 py-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">البريد الإلكتروني</p>
                  <p className="font-medium text-gray-900 dark:text-white" dir="ltr">
                    {user.email}
                  </p>
                </div>
              </div>
            )}

            {/* Phone */}
            {user?.phone && (
              <div className="flex items-center gap-3 py-2">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">رقم الهاتف</p>
                  <p className="font-medium text-gray-900 dark:text-white" dir="ltr">
                    {user.phone}
                  </p>
                </div>
              </div>
            )}

            {/* Address */}
            {user?.address && (
              <div className="flex items-center gap-3 py-2">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <MapPin className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">العنوان</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {user.address}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Settings Section */}
        <div className="card p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
            الإعدادات
          </h3>

          <div className="space-y-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                {darkMode ? (
                  <Moon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                ) : (
                  <Sun className="w-5 h-5 text-yellow-600" />
                )}
                <span className="font-medium text-gray-900 dark:text-white">
                  الوضع الليلي
                </span>
              </div>
              <div
                className={`w-12 h-6 rounded-full transition-colors relative ${
                  darkMode ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </div>
            </button>

            {/* Change Password */}
            <button
              onClick={() => navigate('/profile/change-password')}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors touch-target"
            >
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">
                  تغيير كلمة المرور
                </span>
              </div>
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>

            {/* Edit Profile */}
            <button
              onClick={() => navigate('/profile/edit')}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors touch-target"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">
                  تعديل الملف الشخصي
                </span>
              </div>
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Reports Section */}
        <div className="card p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
            التقارير
          </h3>

          <div className="space-y-2">
            {/* Daily Report */}
            <button
              onClick={() => navigate('/reports/daily')}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors touch-target"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="font-medium text-gray-900 dark:text-white">
                  التقرير اليومي
                </span>
              </div>
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full btn btn-danger flex items-center justify-center gap-2 touch-target"
        >
          <LogOut className="w-5 h-5" />
          <span>تسجيل الخروج</span>
        </button>

        {/* App Version */}
        <div className="text-center mt-6 pb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Delivery System Mobile PWA v1.0.0
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Built with Alshamel Company ⚡
          </p>
        </div>
      </main>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="تسجيل الخروج"
        message="هل أنت متأكد من تسجيل الخروج؟"
        confirmText="تسجيل الخروج"
        cancelText="إلغاء"
        variant="danger"
      />
    </div>
  )
}

export default Profile
